import {
  deleteShipping,
  editShipping,
  fetchShipping,
  updateShipping,
} from "@/store/slices/addressSlice";
import { addCart, fetchCart, removeCart } from "@/store/slices/cartSlice";
import { AppDispatch } from "@/store/store";
import { Shipping } from "@/types/address";
import { AddToCartData, CartState, RemoveCartData } from "@/types/cart";
import { SKU_Details } from "@/types/home";

export const applyRounding = (value: number) => {
  const decimalPart = value % 1;
  return decimalPart < 0.5 ? Math.floor(value) : Math.ceil(value);
};

export const calculateDealerPrice = (
  MRP_price: number,
  NDP_Price: number,
  gst: number
): number => {
  const gstPrice = (NDP_Price * gst) / 100;
  const totalPrice = NDP_Price + gstPrice;
  return isNaN(totalPrice) ? 0 : totalPrice;
};

export function calculateUpdatedDiscountPercentage(
  MRP_price: number,
  NDP_price: number,
  gst: number
) {
  const ndp_with_gst = NDP_price + (NDP_price * gst) / 100;
  // const mrp_without_gst = MRP_price / (1 + gst / 100);
  const discountAmount = MRP_price - ndp_with_gst;
  const discountPercentage = (discountAmount / MRP_price) * 100;
  return applyRounding(discountPercentage);
}

// Function to calculate Offer Price
export const calculateOfferPrice = (
  MRP_price: number,
  Offer_price: number,
  gst: number
): number => {
  const gstPrice = (Offer_price * gst) / 100;
  const totalPrice = Offer_price + gstPrice;
  return isNaN(totalPrice) ? 0 : totalPrice;
};

// Function to check if a product is in the cart
export const isInBulkCart = (
  cart: CartState | null,
  SKU_ID: string
): boolean => {
  if (!cart || !Array?.isArray(cart?.manufacturers)) return false;

  for (const manufacturer of cart.manufacturers) {
    for (const product of manufacturer?.products) {
      if (product?.SKU_ID === SKU_ID) {
        return true;
      }
    }
  }
  return false;
};

export const cartPriceType = (
  cart: CartState | null,
  SKU_ID: string
): "ndp" | "sales" => {
  if (!cart || !Array?.isArray(cart?.manufacturers)) return "ndp";

  for (const manufacturer of cart.manufacturers) {
    for (const product of manufacturer?.products) {
      if (product?.SKU_ID === SKU_ID) {
        return product?.Type;
      }
    }
  }
  return "ndp";
};

export const colourNameToHex = (colour: string): string | null => {
  const colours: { [key: string]: string } = {
    aliceblue: "#f0f8ff",
    antiquewhite: "#faebd7",
    aqua: "#00ffff",
    aquamarine: "#7fffd4",
    azure: "#f0ffff",
    beige: "#f5f5dc",
    bisque: "#ffe4c4",
    black: "#000000",
    "mix colour": "#995F99",
    "black/orange": "#7F5200",
    blaxk: "#000000",
    blanchedalmond: "#ffebcd",
    blue: "#0000ff",
    blueviolet: "#8a2be2",
    brown: "#a52a2a",
    burlywood: "#deb887",
    cadetblue: "#5f9ea0",
    chartreuse: "#7fff00",
    chocolate: "#d2691e",
    coral: "#ff7f50",
    cornflowerblue: "#6495ed",
    cornsilk: "#fff8dc",
    crimson: "#dc143c",
    cyan: "#00ffff",
    darkblue: "#00008b",
    darkcyan: "#008b8b",
    darkgoldenrod: "#b8860b",
    darkgray: "#a9a9a9",
    darkgreen: "#006400",
    darkkhaki: "#bdb76b",
    darkmagenta: "#8b008b",
    darkolivegreen: "#556b2f",
    darkorange: "#ff8c00",
    darkorchid: "#9932cc",
    darkred: "#8b0000",
    darksalmon: "#e9967a",
    darkseagreen: "#8fbc8f",
    darkslateblue: "#483d8b",
    darkslategray: "#2f4f4f",
    darkturquoise: "#00ced1",
    darkviolet: "#9400d3",
    deeppink: "#ff1493",
    deepskyblue: "#00bfff",
    dimgray: "#696969",
    dodgerblue: "#1e90ff",
    firebrick: "#b22222",
    floralwhite: "#fffaf0",
    forestgreen: "#228b22",
    fuchsia: "#ff00ff",
    gainsboro: "#dcdcdc",
    ghostwhite: "#f8f8ff",
    gold: "#ffd700",
    goldenrod: "#daa520",
    gray: "#808080",
    green: "#008000",
    greenyellow: "#adff2f",
    honeydew: "#f0fff0",
    hotpink: "#ff69b4",
    "indianred ": "#cd5c5c",
    indigo: "#4b0082",
    ivory: "#fffff0",
    khaki: "#f0e68c",
    lavender: "#e6e6fa",
    lavenderblush: "#fff0f5",
    lawngreen: "#7cfc00",
    lemonchiffon: "#fffacd",
    lightblue: "#add8e6",
    lightcoral: "#f08080",
    lightcyan: "#e0ffff",
    lightgoldenrodyellow: "#fafad2",
    lightgrey: "#d3d3d3",
    lightgreen: "#90ee90",
    lightpink: "#ffb6c1",
    lightsalmon: "#ffa07a",
    lightseagreen: "#20b2aa",
    lightskyblue: "#87cefa",
    lightslategray: "#778899",
    lightsteelblue: "#b0c4de",
    lightyellow: "#ffffe0",
    lime: "#00ff00",
    limegreen: "#32cd32",
    linen: "#faf0e6",
    magenta: "#ff00ff",
    maroon: "#800000",
    mediumaquamarine: "#66cdaa",
    mediumblue: "#0000cd",
    mediumorchid: "#ba55d3",
    mediumpurple: "#9370d8",
    mediumseagreen: "#3cb371",
    mediumslateblue: "#7b68ee",
    mediumspringgreen: "#00fa9a",
    mediumturquoise: "#48d1cc",
    mediumvioletred: "#c71585",
    midnightblue: "#191970",
    mintcream: "#f5fffa",
    mistyrose: "#ffe4e1",
    moccasin: "#ffe4b5",
    navajowhite: "#ffdead",
    navy: "#000080",
    oldlace: "#fdf5e6",
    olive: "#808000",
    olivedrab: "#6b8e23",
    orange: "#ffa500",
    orangered: "#ff4500",
    orchid: "#da70d6",
    palegoldenrod: "#eee8aa",
    palegreen: "#98fb98",
    paleturquoise: "#afeeee",
    palevioletred: "#d87093",
    papayawhip: "#ffefd5",
    peachpuff: "#ffdab9",
    peru: "#cd853f",
    pink: "#ffc0cb",
    plum: "#dda0dd",
    powderblue: "#b0e0e6",
    purple: "#800080",
    rebeccapurple: "#663399",
    red: "#ff0000",
    rosybrown: "#bc8f8f",
    royalblue: "#4169e1",
    saddlebrown: "#8b4513",
    salmon: "#fa8072",
    sandybrown: "#f4a460",
    seagreen: "#2e8b57",
    seashell: "#fff5ee",
    sienna: "#a0522d",
    silver: "#c0c0c0",
    skyblue: "#87ceeb",
    slateblue: "#6a5acd",
    slategray: "#708090",
    snow: "#fffafa",
    springgreen: "#00ff7f",
    steelblue: "#4682b4",
    tan: "#d2b48c",
    teal: "#008080",
    thistle: "#d8bfd8",
    tomato: "#ff6347",
    turquoise: "#40e0d0",
    violet: "#ee82ee",
    wheat: "#f5deb3",
    white: "#ffffff",
    whitesmoke: "#f5f5f5",
    yellow: "#ffff00",
    yellowgreen: "#9acd32",
    "stone white matt": "#EAEAEA",
    "black matt": "#1E1E1E",
    "black-anthracite matt": "#2B2B2B",
    "white pink glossy": "#FFDDFF",
    "neon yellow glossy": "#FFFF33",
    "neon yellow - black glossy": "#E3E100",
    "white glossy": "#F8F8F8",
    "white black glossy": "#1C1C1C",
    "grey matt": "#999999",
    "black blue matt": "#23234D",
    "black lime matt": "#34563D",
    "black red matt": "#3B2323",
    "ocean blue matt": "#335656",
    "black finish w// decal kit": "#242424",
    "blast grey": "#808080",
    "blast orange": "#FFB347",
    "blast black": "#1C1C1C",
    "blast red": "#FF4C4C",
    grey: "#808080",
    "bead blast black w/mirror polish": "#0F0F0F",
    "bead blast orange w/mirror polish": "#6C3F00",
    "bead blast red w/mirror polish": "#4C0C0C",
    "ano. black": "#1C1C1C",
    "anod. black": "#1C1C1C",
    "anod. red": "#B30000",
    "blast silver": "#C0C0C0",
    "blast blue": "#0000FF",
    granite: "#828282",
    "pearl red": "#B93A32",
    "militry green": "#4B5320",
    "ocean blue": "#0077BE",
    "bottle green": "#006A4E",
    "mauve red": "#D473D4",
    "emerald green": "#50C878",
    "sapphire blue": "#0F52BA",
  };

  return colours[colour?.toLowerCase()] || colour?.toLowerCase() || "";
};

export const formatWithCommasWithDecimal = (num: number | string): string => {
  const formattedNumber = parseFloat(num.toString()).toFixed(2); // Convert to float to handle decimal numbers
  const parts = formattedNumber.split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1] ? `.${parts[1]}` : ""; // Check if decimal part exists

  let formattedIntegerPart = "";
  if (integerPart.length <= 3) {
    formattedIntegerPart = integerPart;
  } else if (integerPart.length <= 5) {
    formattedIntegerPart =
      integerPart.substring(0, integerPart.length - 3) +
      "," +
      integerPart.substring(integerPart.length - 3);
  } else if (integerPart.length <= 7) {
    formattedIntegerPart =
      integerPart.substring(0, integerPart.length - 5) +
      "," +
      integerPart.substring(integerPart.length - 5, integerPart.length - 3) +
      "," +
      integerPart.substring(integerPart.length - 3);
  } else {
    formattedIntegerPart = integerPart.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  }

  return `${formattedIntegerPart}${decimalPart}`;
};

export const formatWithCommasWithoutDecimal = (
  num: number | string
): string => {
  const formattedNumber = parseFloat(num.toString()).toFixed(2); // Convert to float to handle decimal numbers
  const parts = formattedNumber.split(".");
  const integerPart = parts[0];

  let formattedIntegerPart = "";
  if (integerPart.length <= 3) {
    formattedIntegerPart = integerPart;
  } else if (integerPart.length <= 5) {
    formattedIntegerPart =
      integerPart.substring(0, integerPart.length - 3) +
      "," +
      integerPart.substring(integerPart.length - 3);
  } else if (integerPart.length <= 7) {
    formattedIntegerPart =
      integerPart.substring(0, integerPart.length - 5) +
      "," +
      integerPart.substring(integerPart.length - 5, integerPart.length - 3) +
      "," +
      integerPart.substring(integerPart.length - 3);
  } else {
    formattedIntegerPart = integerPart.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
  }

  return formattedIntegerPart;
};

export const handleAddToCart = (
  dispatch: AppDispatch,
  skuid: string,
  phoneNumber: string | null,
  quantity: number,
  selected: "ndp" | "sales",
  handleClose: () => void
): void => {
  const bulkCartData: AddToCartData = {
    PhoneNumber: phoneNumber,
    SKU_ID: skuid,
    quantity: quantity,
    Type: selected,
  };

  dispatch(addCart(bulkCartData)).then(() => {
    dispatch(fetchCart(phoneNumber)).then(() => {
      handleClose();
    });
  });
};

export const handleRemoveFromCart = (
  dispatch: AppDispatch,
  skuid: string,
  phoneNumber: string | null,
  handleClose?: () => void,
  setOpenDialog?: (id: boolean) => void
) => {
  const bulkCartData: RemoveCartData = {
    PhoneNumber: phoneNumber,
    SKU_ID: skuid,
  };
  dispatch(removeCart(bulkCartData)).then(() => {
    dispatch(fetchCart(phoneNumber)).then(() => {
      if (handleClose) {
        handleClose();
      }
      if (setOpenDialog) {
        setOpenDialog(false);
      }
    });
  });
};

export const getCurrentQuantity = (
  SKU_ID: string,
  cart: CartState | null
): number => {
  if (!cart || !Array?.isArray(cart?.manufacturers)) return 0;

  // Loop through manufacturers
  for (const manufacturer of cart.manufacturers) {
    // Search for the product with the matching SKU_ID within the manufacturer's products array
    const product = manufacturer?.products.find(
      (item) => item?.SKU_ID === SKU_ID
    );

    // If the product is found, return its buy_quantity, defaulting to 0 if not available
    if (product) {
      return product?.buy_quantity || 0;
    }
  }

  // Return 0 if the product is not found in any manufacturer
  return 0;
};

export const calculateNetTotal = (
  NDP_Price: string,
  GST: number,
  currentQuantity: number
): number => {
  const total = parseInt(NDP_Price, 10) * currentQuantity;
  const gstAmount = (total * GST) / 100;
  return total + gstAmount;
};

export const calculateNDPPriceWithGST = (NDP_Price: string, GST: number) => {
  const ndp = parseInt(NDP_Price);
  return ndp + (ndp * GST) / 100;
};

export const getTotalCartQuantity = (cart: CartState | null): number => {
  if (!cart || !Array?.isArray(cart?.manufacturers)) return 0;
  return cart?.manufacturers?.reduce((totalQty, manufacturer) => {
    return (
      totalQty +
      manufacturer?.products?.reduce(
        (brandTotal, product) => brandTotal + product?.buy_quantity,
        0
      )
    );
  }, 0);
};

// Function to calculate total amount including product-specific GST
export const calculateTotalAmountWithGST = (cart: CartState | null): number => {
  if (!cart || !Array.isArray(cart.manufacturers)) return 0;

  const totalPrice = cart.manufacturers.reduce((acc, manufacturer) => {
    return (
      acc +
      manufacturer.products.reduce((brandTotal, product) => {
        const ndpPrice = parseFloat(product?.NDP_Price); // Base price
        const salesPrice = parseFloat(product?.Sales_Price); // Discounted price
        const salesQuantity = Number(product?.Sales_Qunatity); // Quantity to unlock Sales_Price
        const buyQuantity = Number(product?.buy_quantity); // User selected quantity
        const gstRate = Number(product?.GST); // GST percentage

        // Choose the correct price
        const effectivePrice =
          buyQuantity &&
          salesQuantity !== null &&
          buyQuantity >= salesQuantity &&
          salesQuantity > 0
            ? salesPrice
            : ndpPrice;

        const gstAmount = (effectivePrice * gstRate) / 100; // GST per unit
        const totalPriceWithGST = (effectivePrice + gstAmount) * buyQuantity; // Total price for the product

        return brandTotal + totalPriceWithGST;
      }, 0)
    );
  }, 0);

  return totalPrice;
};

export const getStateId = (stateName: string): string => {
  const stateMap: Record<string, string> = {
    "Andhra Pradesh": "1",
    "Arunachal Pradesh": "2",
    Assam: "3",
    Bihar: "4",
    Chhattisgarh: "5",
    Goa: "6",
    Gujarat: "7",
    Haryana: "8",
    "Himachal Pradesh": "9",
    Jharkhand: "10",
    Karnataka: "11",
    Kerala: "12",
    "Madhya Pradesh": "13",
    Maharashtra: "14",
    Manipur: "15",
    Meghalaya: "16",
    Mizoram: "17",
    Nagaland: "18",
    Odisha: "19",
    Punjab: "20",
    Rajasthan: "21",
    Sikkim: "22",
    "Tamil Nadu": "23",
    Telangana: "24",
    Tripura: "25",
    "Uttar Pradesh": "26",
    Uttarakhand: "27",
    "West Bengal": "28",
    Chandigarh: "29",
    Delhi: "30",
    Ladakh: "31",
    "Jammu and Kashmir": "32",
  };

  return stateMap[stateName] || "";
};

export const getStateName = (stateId: number | string): string => {
  const states: Record<string, string> = {
    "1": "Andhra Pradesh",
    "2": "Arunachal Pradesh",
    "3": "Assam",
    "4": "Bihar",
    "5": "Chhattisgarh",
    "6": "Goa",
    "7": "Gujarat",
    "8": "Haryana",
    "9": "Himachal Pradesh",
    "10": "Jharkhand",
    "11": "Karnataka",
    "12": "Kerala",
    "13": "Madhya Pradesh",
    "14": "Maharashtra",
    "15": "Manipur",
    "16": "Meghalaya",
    "17": "Mizoram",
    "18": "Nagaland",
    "19": "Odisha",
    "20": "Punjab",
    "21": "Rajasthan",
    "22": "Sikkim",
    "23": "Tamil Nadu",
    "24": "Telangana",
    "25": "Tripura",
    "26": "Uttar Pradesh",
    "27": "Uttarakhand",
    "28": "West Bengal",
    "29": "Chandigarh",
    "30": "Delhi",
    "31": "Ladakh",
    "32": "Jammu and Kashmir",
  };

  return states[stateId.toString()] || "";
};

export const validatePAN = (pan: string): boolean => {
  const regex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return regex.test(pan.toUpperCase());
};

export const validateAadhaar = (aadhar: string): boolean => {
  const regex = /^\d{12}$/;
  return regex.test(aadhar.trim());
};

export const validateGSTIN = (gstin: string): boolean => {
  const regex =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  return regex.test(gstin.toUpperCase());
};

export const handleAadharInput = (
  e: React.ChangeEvent<HTMLInputElement>
): void => {
  e.target.value = e.target.value.replace(/\D/g, "").slice(0, 12); // Numbers only, max length 12
};

export const handleGSTInput = (
  e: React.ChangeEvent<HTMLInputElement>
): void => {
  e.target.value = e.target.value
    .toUpperCase()
    .replace(/\s+/g, "")
    .slice(0, 15); // Uppercase, no spaces, max length 15
};

export const handlePANInput = (
  e: React.ChangeEvent<HTMLInputElement>
): void => {
  e.target.value = e.target.value
    .toUpperCase()
    .replace(/\s+/g, "")
    .slice(0, 10); // Uppercase, no spaces, max length 10
};

export const handleBlur = () => {
  return;
};

export const handleModify = (
  address: Shipping,
  bulkUserID: string | null,
  dispatch: AppDispatch,
  handleOpen: (id: boolean) => void,
  handleClose: (id: boolean) => void
) => {
  if (!address || !bulkUserID) return;
  handleOpen(true);
  const addressData = {
    data: {
      is_default: address.is_default === 1 ? "no" : "yes",
    },
    shippingID: address.shippingID,
  };

  dispatch(updateShipping(addressData)).then(() => {
    dispatch(fetchShipping(bulkUserID)).then(() => {
      handleClose(false);
    });
  });
};

export const handleEditShipping = (
  address: Shipping,
  bulkUserID: string | null,
  dispatch: AppDispatch
) => {
  if (!address || !bulkUserID) return;

  const addressData = {
    data: {
      is_default: address.is_default === 1 ? "no" : "yes",
    },
    shippingID: address.shippingID,
  };

  dispatch(editShipping(addressData)).then(() => {
    dispatch(fetchShipping(bulkUserID));
  });
};

// Remove Shipping Address Function
export const handleRemoveShipping = (
  shippingID: number,
  bulkUserID: string | null,
  dispatch: AppDispatch,
  handleOpen: (id: boolean) => void,
  handleClose: (id: boolean) => void
) => {
  if (!shippingID || !bulkUserID) return;
  handleOpen(true);

  dispatch(deleteShipping(shippingID)).then(() => {
    dispatch(fetchShipping(bulkUserID)).then(() => {
      handleClose(false);
    });
  });
};

export const getNDPPriceAndQty = (
  SKU_Details: SKU_Details[],
  SKU_ID: string
) => {
  const sku = SKU_Details?.find((item) => item.SKU_ID === SKU_ID);
  if (!sku) {
    return { NDP_price: 0, qty: 0 }; // Default values if not found
  }
  return { NDP_price: sku.NDP_price, qty: sku.qty };
};
